import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@repo/ui/components/button';
import { FormError } from '@repo/ui/components/form-error';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@repo/ui/components/sheet';
import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { SelectableDateCard } from './selectable-date-card';

export interface DateItem {
  title: string;
  date: string;
}

const SUGGESTED_DATES: DateItem[] = [
  { title: 'Dia dos Namorados', date: '2025-06-12' },
  { title: 'Natal', date: '2025-12-25' },
];

const customDateSchema = z.object({
  title: z.string().min(1, 'O título é obrigatório'),
  date: z.string().min(1, 'A data é obrigatória'),
});

type CustomDateFormValues = z.infer<typeof customDateSchema>;

interface DatesStepProps {
  title?: string;
  subtitle?: string;
  defaultValue?: DateItem[];
  isLoading?: boolean;
  continueLabel?: string;
  loadingLabel?: string;
  onNext: (dates: DateItem[]) => void;
  onBack: () => void;
}

export function DatesStep({
  title = 'Datas especiais',
  subtitle = 'Selecione as datas que você quer lembrar.',
  defaultValue = [],
  isLoading = false,
  continueLabel = 'Continuar',
  loadingLabel = 'Salvando...',
  onNext,
  onBack,
}: DatesStepProps) {
  const [selectedDates, setSelectedDates] = useState<DateItem[]>(() => {
    if (defaultValue.length > 0) {
      return defaultValue;
    }
    const firstDate = SUGGESTED_DATES[0];
    return firstDate ? [firstDate] : [];
  });
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CustomDateFormValues>({
    resolver: zodResolver(customDateSchema),
  });

  const toggleSuggestedDate = (date: DateItem) => {
    const exists = selectedDates.some(
      (d) => d.title === date.title && d.date === date.date,
    );
    if (exists) {
      setSelectedDates(
        selectedDates.filter(
          (d) => !(d.title === date.title && d.date === date.date),
        ),
      );
    } else {
      setSelectedDates([...selectedDates, date]);
    }
  };

  const addCustomDate = (data: CustomDateFormValues) => {
    setSelectedDates([...selectedDates, data]);
    reset();
    setIsModalOpen(false);
  };

  const removeDate = (index: number) => {
    setSelectedDates(selectedDates.filter((_, i) => i !== index));
  };

  const handleNext = () => {
    if (selectedDates.length === 0) {
      return;
    }
    onNext(selectedDates);
  };

  const isSelected = (date: DateItem) =>
    selectedDates.some((d) => d.title === date.title && d.date === date.date);

  const customDates = selectedDates.filter(
    (d) =>
      !SUGGESTED_DATES.some((s) => s.title === d.title && s.date === d.date),
  );

  return (
    <div className="space-y-6">
      <div className="space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        <p className="text-muted-foreground text-sm">{subtitle}</p>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium">Datas sugeridas</p>
        <div className="space-y-3">
          {SUGGESTED_DATES.map((date) => (
            <SelectableDateCard
              key={date.title}
              title={date.title}
              date={date.date}
              selected={isSelected(date)}
              disabled={isLoading}
              onToggle={() => toggleSuggestedDate(date)}
            />
          ))}
        </div>
      </div>

      {customDates.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium">Datas personalizadas</p>
          {customDates.map((date) => (
            <div
              key={`${date.title}-${date.date}`}
              className="flex items-center justify-between rounded-lg border p-3"
            >
              <div>
                <p className="font-medium">{date.title}</p>
                <p className="text-muted-foreground text-sm">
                  {new Date(date.date + 'T00:00:00').toLocaleDateString(
                    'pt-BR',
                    {
                      day: '2-digit',
                      month: 'short',
                    },
                  )}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={isLoading}
                onClick={() =>
                  removeDate(
                    selectedDates.findIndex(
                      (d) => d.title === date.title && d.date === date.date,
                    ),
                  )
                }
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <Button
        type="button"
        variant="outline"
        className="w-full"
        disabled={isLoading}
        onClick={() => setIsModalOpen(true)}
      >
        <Plus className="mr-2 h-4 w-4" />
        Adicionar data personalizada
      </Button>

      <Sheet
        open={isModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            reset();
          }
          setIsModalOpen(open);
        }}
      >
        <SheetContent
          side="bottom"
          className="h-auto max-h-[90vh] overflow-y-auto"
        >
          <SheetHeader>
            <SheetTitle>Adicionar data personalizada</SheetTitle>
            <SheetDescription>
              Adicione uma data especial para lembrar.
            </SheetDescription>
          </SheetHeader>
          <form
            onSubmit={handleSubmit(addCustomDate)}
            className="mt-6 space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="custom-title">Título</Label>
              <Input
                id="custom-title"
                placeholder="Ex: Aniversário de namoro"
                disabled={isLoading}
                {...register('title')}
              />
              <FormError message={errors.title?.message} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="custom-date">Data</Label>
              <Input
                id="custom-date"
                type="date"
                disabled={isLoading}
                {...register('date')}
              />
              <FormError message={errors.date?.message} />
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                disabled={isLoading}
                onClick={() => {
                  reset();
                  setIsModalOpen(false);
                }}
              >
                Cancelar
              </Button>
              <Button type="submit" className="flex-1" disabled={isLoading}>
                Adicionar
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      <div className="flex gap-3">
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={onBack}
          disabled={isLoading}
        >
          Voltar
        </Button>
        <Button
          type="button"
          className="flex-1"
          onClick={handleNext}
          disabled={selectedDates.length === 0 || isLoading}
        >
          {isLoading ? loadingLabel : continueLabel}
        </Button>
      </div>

      <FormError
        message={
          selectedDates.length === 0
            ? 'Selecione pelo menos uma data'
            : undefined
        }
        className="text-center"
      />
    </div>
  );
}
